import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../auth';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Save, FileText, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const SLUGS = [
  { slug: 'terms', label: '이용약관 · Terms', accent: '#556B2F' },
  { slug: 'privacy', label: '개인정보처리방침 · Privacy', accent: '#1A1A1A' },
  { slug: 'refund', label: '교환·환불 정책 · Refund', accent: '#8E2A4E' },
];
const LANGS = [
  { code: 'ko', label: '한국어 *', required: true },
  { code: 'ru', label: 'Русский' },
  { code: 'en', label: 'English' },
  { code: 'uz', label: "O'zbekcha" },
];

export default function LegalEditorPage() {
  const [activeSlug, setActiveSlug] = useState('terms');
  const [activeLang, setActiveLang] = useState('ko');
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/legal/${activeSlug}`)
      .then(r => setPage(r.data))
      .catch(() => setPage(null));
  }, [activeSlug]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/legal/${activeSlug}`, page);
      toast.success('Сохранено');
    } catch { toast.error('Ошибка'); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setPage({ ...page, [k]: v });

  return (
    <div className="space-y-6 max-w-5xl" data-testid="legal-editor-page">
      <div>
        <p className="eyebrow mb-3">Юридические документы</p>
        <h1 className="h-display text-4xl md:text-5xl">Редактор <span className="text-[#C9A352]">документов</span></h1>
        <p className="text-[#1A1A1A]/55 mt-2">
          Корейский текст (한국어) обязателен — его проверяет Toss Payments. Остальные языки опциональны.
        </p>
      </div>

      {/* Slug tabs */}
      <div className="flex flex-wrap gap-2" data-testid="slug-tabs">
        {SLUGS.map(s => (
          <button
            key={s.slug}
            onClick={() => setActiveSlug(s.slug)}
            data-testid={`tab-${s.slug}`}
            className={`px-5 py-2.5 rounded-md text-sm font-bold transition-colors ${
              activeSlug === s.slug
                ? 'bg-[#1A1A1A] text-[#F6F4EE]'
                : 'bg-[#F2EFE6] text-[#1A1A1A]/70 hover:bg-[#F2EFE6]/80'
            }`}
          >
            {s.label}
          </button>
        ))}
        <Link to={`/${activeSlug === 'refund' ? 'refund-policy' : activeSlug}`} target="_blank" data-testid="preview-link">
          <Button variant="outline" size="sm" className="cff-button-outline h-[42px]">
            <ExternalLink className="h-3 w-3 mr-2" /> Предпросмотр
          </Button>
        </Link>
      </div>

      {!page ? (
        <div className="text-[#1A1A1A]/40">Loading…</div>
      ) : (
        <>
          {/* Lang tabs */}
          <div className="flex flex-wrap gap-2 border-b border-[#1A1A1A]/10 pb-2">
            {LANGS.map(l => (
              <button
                key={l.code}
                onClick={() => setActiveLang(l.code)}
                data-testid={`lang-tab-${l.code}`}
                className={`px-4 py-1.5 text-sm font-semibold ${
                  activeLang === l.code
                    ? 'text-[#1A1A1A] border-b-2 border-[#556B2F] -mb-[10px]'
                    : 'text-[#1A1A1A]/45 hover:text-[#1A1A1A]/70'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <Card className="p-6 md:p-8 space-y-5">
            <div>
              <Label className="text-sm font-semibold">Title ({activeLang.toUpperCase()})</Label>
              <Input
                value={page[`title_${activeLang}`] || ''}
                onChange={(e) => set(`title_${activeLang}`, e.target.value)}
                className="mt-2 h-11 border-[#1A1A1A]/15 focus:ring-[#556B2F]"
                data-testid={`title-${activeLang}`}
              />
            </div>
            <div>
              <Label className="text-sm font-semibold">Body ({activeLang.toUpperCase()})</Label>
              <Textarea
                rows={28}
                value={page[`body_${activeLang}`] || ''}
                onChange={(e) => set(`body_${activeLang}`, e.target.value)}
                className="mt-2 border-[#1A1A1A]/15 focus:ring-[#556B2F] font-mono text-sm leading-relaxed"
                data-testid={`body-${activeLang}`}
                placeholder={activeLang === 'ko' ? '필수 — 토스페이먼츠 심사 대상' : 'Опционально'}
              />
              <p className="text-xs text-[#1A1A1A]/40 mt-1.5">
                <FileText className="h-3 w-3 inline mr-1" />
                Поддерживается plain text с переносами строк. HTML не парсится.
              </p>
            </div>
          </Card>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="cff-button-primary h-11 px-8" data-testid="legal-save">
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Сохраняем…' : 'Сохранить'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
